import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Flag, Zap, Star, Rocket, ChevronRight, Watch,
  Trophy, MessageCircle, Library, Clock, Scale
} from 'lucide-react';
import { COLORS, TIER_NAMES, TIER_COLORS, MOCK_CONTENT } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { fetchGoals } from '../services/api';
import VideoWithSoundToggle from '../components/VideoWithSoundToggle';

export default function DashboardPage() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);

  useEffect(() => {
    fetchGoals().then(setGoals).catch(() => {});
  }, []);

  if (!user) return null;
  const tierIdx = user.membership_tier || 0;

  return (
    <div style={{ maxWidth: 840, margin: '0 auto', padding: '16px 24px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <p style={{ fontSize: 15, color: COLORS.textSecondary, margin: 0 }}>Welcome back,</p>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>{user.name}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: TIER_COLORS[tierIdx] || COLORS.primary, padding: '6px 12px', borderRadius: 9999 }}>
            <Star size={14} color="#FFF" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#FFF' }}>{TIER_NAMES[tierIdx] || 'Free'}</span>
          </div>
        </div>
      </div>

      {/* INTRO VIDEO (members-only, 60% size) */}
      <div data-testid="dashboard-intro-video-wrap" style={{ marginBottom: 16 }}>
        <VideoWithSoundToggle
          src="/media/dashboard-intro.mp4"
          testId="dashboard-intro-video"
          aspectRatio="16 / 9"
          maxWidth={504}
          objectFit="cover"
        />
      </div>

      {/* Trial Banner */}
      {user.trial_active && (
        <Link to="/dashboard/profile" style={{ display: 'block', borderRadius: 16, overflow: 'hidden', backgroundColor: '#1E88E5', marginBottom: 16, textDecoration: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: 12, gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={20} color="#FFF" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#FFF' }}>7-Day Free Trial Active</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>You have full Analyst features! Expires {user.trial_end ? new Date(user.trial_end).toLocaleDateString() : 'soon'}</div>
            </div>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.25)', padding: '8px 14px', borderRadius: 9999 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#FFF' }}>Upgrade</span>
            </div>
          </div>
        </Link>
      )}

      {/* Stats Row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        {[
          { icon: Flag, color: COLORS.primary, num: goals.length, label: 'Active Goals' },
          { icon: Zap, color: COLORS.secondary, num: 12, label: 'Activities' },
          { icon: Star, color: COLORS.tierOptimizer, num: `Tier ${tierIdx}`, label: 'Membership' },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, backgroundColor: COLORS.surface, borderRadius: 16, padding: 16, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <s.icon size={22} color={s.color} />
            <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.textPrimary, marginTop: 8, fontFamily: 'Outfit, sans-serif' }}>{s.num}</div>
            <div style={{ fontSize: 11, color: COLORS.textSecondary, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Upgrade Card */}
      <Link to="/dashboard/profile" style={{ display: 'flex', alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 16, padding: 16, border: `1px solid ${COLORS.tierElite}`, marginBottom: 16, textDecoration: 'none' }}>
        <Rocket size={28} color={COLORS.tierElite} />
        <div style={{ flex: 1, marginLeft: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>Upgrade Your Membership</div>
          <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 2 }}>Unlock AI coaching, personalized insights & more</div>
        </div>
        <ChevronRight size={20} color={COLORS.textSecondary} />
      </Link>

      {/* Quick Actions */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 16, fontFamily: 'Outfit, sans-serif' }}>Quick Actions</h2>
        <div style={{ display: 'flex', gap: 12 }}>
          {[
            { icon: Flag, bg: '#E8F5E9', color: COLORS.primary, label: 'Set Goals', link: '/dashboard/goals' },
            { icon: MessageCircle, bg: '#E3F2FD', color: COLORS.secondary, label: 'AI Coach', link: '/dashboard/chat' },
            { icon: Scale, bg: '#E8F0F7', color: '#4682B4', label: 'Body Comp', link: '/dashboard/profile/body-composition' },
            { icon: Library, bg: '#FFF3E0', color: COLORS.accentWarm, label: 'Library', link: '/dashboard' },
          ].map((a, i) => (
            <Link key={i} to={a.link} data-testid={`quick-action-${a.label.toLowerCase().replace(/\s+/g, '-')}`} style={{ textAlign: 'center', textDecoration: 'none' }}>
              <div style={{ width: 52, height: 52, borderRadius: 16, backgroundColor: a.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px' }}>
                <a.icon size={22} color={a.color} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary }}>{a.label}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Feature Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {[
          { icon: Watch, iconColor: '#4CAF50', bg: '#E8F5E9', title: 'Health Monitoring', desc: 'Track steps, heart rate, sleep & more' },
          { icon: Trophy, iconColor: '#FF9800', bg: '#FFF3E0', title: 'Community Challenges', desc: 'Join challenges & compete on leaderboards' },
        ].map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 16, padding: 16, gap: 12, cursor: 'pointer', transition: 'transform 0.2s' }} className="goal-card">
            <div style={{ width: 48, height: 48, borderRadius: 14, backgroundColor: f.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <f.icon size={24} color={f.iconColor} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.textPrimary }}>{f.title}</div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>{f.desc}</div>
            </div>
            <ChevronRight size={18} color={COLORS.textSecondary} />
          </div>
        ))}
      </div>

      {/* Recent Goals */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 16, fontFamily: 'Outfit, sans-serif' }}>Recent Goals</h2>
        {goals.length === 0 ? (
          <p style={{ fontSize: 14, color: COLORS.textSecondary }}>No goals yet. <Link to="/dashboard/goals" style={{ color: COLORS.primary, fontWeight: 600 }}>Create one</Link></p>
        ) : goals.slice(0, 3).map((goal, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', backgroundColor: COLORS.surface, borderRadius: 8, padding: 16, marginBottom: 8, gap: 12 }} className="goal-card">
            <div style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: COLORS.primary }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.textPrimary }}>{goal.title}</div>
              <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2, textTransform: 'capitalize' }}>{(goal.category || '').replace('_', ' ')} &bull; {goal.status}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Research & Content */}
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 16, fontFamily: 'Outfit, sans-serif' }}>Research & Content</h2>
        {MOCK_CONTENT.map((item, i) => (
          <div key={i} style={{ backgroundColor: COLORS.surface, borderRadius: 16, padding: 16, marginBottom: 12, cursor: 'pointer' }} className="goal-card">
            <span style={{ display: 'inline-block', backgroundColor: COLORS.surfaceSecondary, padding: '4px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 600, color: COLORS.primary, marginBottom: 8 }}>Tier {item.tier_required}+</span>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 6 }}>{item.title}</div>
            <p style={{ fontSize: 14, color: COLORS.textSecondary, lineHeight: 1.4, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
